import { useResourceStore } from '@/stores/resourceStore';
import { useUser } from '@/stores/userStore';

let knownBackendVersion: string | null = null;

// Every authenticated request shares one abort controller so that logging out
// can cancel all of them in a single call — see cancelInFlightRequests below.
let inFlightRequests = new AbortController();

/**
 * Cancel every request currently in flight.
 *
 * Called on logout. Without this, a request that was already on its way when
 * the user logged out comes back afterwards and its caller writes the response
 * into the store — repopulating the user (and localStorage, via persist) after
 * they've been cleared and sent to the login page.
 *
 * A cancelled request surfaces to its caller as a normal failed response, so
 * callers need no special handling.
 */
export function cancelInFlightRequests(): void {
	inFlightRequests.abort();
	// An aborted controller stays aborted, so replace it rather than reuse it —
	// otherwise every subsequent request would be cancelled before it started.
	inFlightRequests = new AbortController();
}

/**
 * End a session the backend has just told us is already over.
 *
 * An AuthException comes back as a 401 carrying `authError: true` — the token
 * expired, or the account behind it was deleted, or it was unlinked from its
 * school. It has to be acted on from the *status*, above the `!res.ok` return in
 * each caller, which is why the body flag went unread for as long as it did: the
 * backend added the status alongside it, and every caller has returned one
 * branch earlier ever since.
 *
 * Nothing else notices. Tokens last a year and the router guard only tests that
 * one is present, so a dead session survives in localStorage indefinitely: the
 * app keeps rendering the persisted user, from their name to their school to
 * their nav, while every request it makes 401s.
 *
 * A public route is logged out where it stands rather than sent to the login
 * page, for the same reason the router guard exempts those: a page a logged out
 * stranger may open is a page this user may still open.
 *
 * The response goes back blank, like a cancelled one — the login page is already
 * loading, so an alert() of the 401 body on top of that is noise.
 */
function handleAuthFailure<T>(): RequestResponse<T> {
	const user = useUser();

	// A 401 with no session to end is a public endpoint refusing an anonymous
	// caller. There is nothing to clear and nobody to send anywhere.
	if (user.isLoggedIn) {
		const currentRoute = user.$router.currentRoute.value;

		user.logout(currentRoute.meta.isPublic ? null : { name: 'login', query: { redirect: currentRoute.fullPath } });
	}

	return <RequestResponse<T>>{
		success: false,
		data: null,
		message: '',
	};
}

function failedResponse<T>(error: unknown): RequestResponse<T> {
	// A cancelled request is not a failure worth telling the user about — they
	// logged out. Leave the message blank, because some callers alert() it.
	const wasCancelled = (error as Error)?.name === 'AbortError';

	return <RequestResponse<T>>{
		success: false,
		data: null,
		message: wasCancelled ? '' : `Request failed: ${(error as Error).message}`,
	};
}

function checkBackendVersion(res: globalThis.Response): void {
	const version = res.headers.get('X-App-Version');
	if (!version) return;

	// The resource content epoch doesn't bump on deploys, so a deploy that
	// changes the resource payload shape wouldn't otherwise invalidate the
	// persisted resource cache. If the app version has changed since the cached
	// resources were loaded, drop the cache so they refetch on the new version.
	// (For breaking shape changes, also bump the resourceStore persist key.)
	const resourceStore = useResourceStore();
	if (resourceStore.appVersion && resourceStore.appVersion !== version) {
		resourceStore.cache = 0;
	}
	resourceStore.appVersion = version;

	if (knownBackendVersion === null) {
		knownBackendVersion = version;
	} else if (knownBackendVersion !== version) {
		window.location.reload();
	}
}

export const wpApiRequest = async <T>(path: string, data: object) => {
	const url = `${import.meta.env.VITE_API_URL}wp-json${path}`;

	const params = {
		method: 'POST',
		body: JSON.stringify(data),
		headers: {
			'Content-Type': 'application/json',
		},
	};

	// Never rejects — login() sets loginLoading before awaiting this and clears it
	// after, so a throw would leave the login form stuck in its loading state.
	try {
		const res = await fetch(url, params);

		checkBackendVersion(res);

		if (!res.ok) {
			return <RequestResponse<T>>{
				success: false,
				data: null,
				message: 'Error' + (await res.text()),
			};
		}

		return (await res.json()) as RequestResponse<T>;
	} catch (error) {
		return failedResponse<T>(error);
	}
};

export const request = async <T>(contorller: string, method: string, data?: RequestData): Promise<RequestResponse<T>> => {
	const url = new URL(`${import.meta.env.VITE_API_URL}wp-admin/admin-ajax.php?action=api&controller=${contorller}&method=${method}`);

	const params = {
		method: 'POST',
		headers: new Headers(),
		body: null as null | string,
		signal: inFlightRequests.signal,
	};

	const user = useUser();

	if (user.school?.hs_object_id) {
		data = data || {};
		data.hsSchoolId = user.school.hs_object_id;
	}

	if (data) {
		params.method = 'POST';
		params.headers.set('Content-Type', 'application/json');
		params.body = JSON.stringify(data);
	}

	if (user.token) {
		params.headers.set('Authorization', `Bearer ${user.token}`);
	}

	// Everything sits inside the try so this never rejects: reading the body can
	// fail as well as the fetch itself (connection dropped mid-download, or a
	// non-JSON error page from the host). Callers depend on always getting a
	// response object back — several set a loading flag before awaiting and clear
	// it after, and a throw here would leave that flag stuck true for the session.
	try {
		const res = await fetch(url, params);

		checkBackendVersion(res);

		if (res.status === 401) {
			return handleAuthFailure<T>();
		}

		if (!res.ok) {
			return <RequestResponse<T>>{
				success: false,
				data: null,
				message: 'Error' + (await res.text()),
			};
		}

		return await (res.json() as Promise<RequestResponse<T>>);
	} catch (error) {
		return failedResponse<T>(error);
	}
};

/**
 * `fields` are sent alongside the file as ordinary form fields, which the
 * backend binds to the method's own parameters — `Utilities::GetParam()` reads
 * `$_POST` before anything else, so a field named for a parameter arrives as
 * that parameter. Booleans go as `'true'`/`'false'`: the backend coerces a
 * `bool` parameter with `filter_var(FILTER_VALIDATE_BOOLEAN)`, which reads
 * those and would read `'0'`/`'1'` too.
 */
export const uploadFile = async <T>(controller: string, method: string, file: File, fields: Record<string, string> = {}) => {
	const url = new URL(`${import.meta.env.VITE_API_URL}wp-admin/admin-ajax.php?action=api&controller=${controller}&method=${method}`);

	const formData = new FormData();
	formData.append('file', file);

	for (const [name, value] of Object.entries(fields)) {
		formData.append(name, value);
	}

	const user = useUser();

	if (user.school?.hs_object_id && !formData.has('hsSchoolId')) {
		formData.append('hsSchoolId', user.school.hs_object_id);
	}

	const params = {
		method: 'POST',
		headers: new Headers(),
		body: formData,
		signal: inFlightRequests.signal,
	};

	if (user.token) {
		params.headers.set('Authorization', `Bearer ${user.token}`);
	}

	// Never rejects, for the same reasons as request() above.
	try {
		const res = await fetch(url, params);

		checkBackendVersion(res);

		if (res.status === 401) {
			return handleAuthFailure<T>();
		}

		if (!res.ok) {
			return <RequestResponse<T>>{
				success: false,
				data: null,
				message: 'Error' + (await res.text()),
			};
		}

		return await (res.json() as Promise<RequestResponse<T>>);
	} catch (error) {
		return failedResponse<T>(error);
	}
};

// eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
interface RequestData extends Object {
	hsSchoolId?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	[key: string]: any;
}

export type RequestResponse<T> = {
	success: boolean;
	data: T;
	message: string;
	// Sent by the backend on the body of a 401. Nothing reads it — the status is
	// what handleAuthFailure acts on, since it is available before the body is
	// parsed — but it is part of the response shape and shows up when one is
	// inspected.
	authError?: boolean;
};

export interface FileUploadResponse {
	fileName: string;
}
